// src/pages/BoatShowsPage.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Container,
  Alert,
  Pagination,
} from "@mui/material";
import { Add, Edit, Delete, Visibility } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#C4B08C" },
    error: { main: "#d32f2f" },
    background: { default: "#FFFFFF", paper: "#FFFFFF" },
    text: { primary: "#111111", secondary: "#555555" },
  },
  typography: { fontFamily: '"Inter", sans-serif' },
});

interface BoatShow {
  id: number;
  name: string;
  image?: string;
}

const BoatShowsPage = () => {
  const [boatShows, setBoatShows] = useState<BoatShow[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedBoatShow, setSelectedBoatShow] = useState<BoatShow | null>(
    null
  );
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 6;
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const API_IMG = process.env.REACT_APP_Image;

  const fetchBoatShows = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/boatshows`);
      const data = await res.json();
      console.log(data);
      setBoatShows(data);
    } catch (err) {
      setAlert({ type: "error", message: "Failed to fetch boat shows" });
    }
  };

  useEffect(() => {
    fetchBoatShows();
  }, []);

  const handleSave = async () => {
    try {
      const formData = new FormData();
      if (selectedBoatShow?.name)
        formData.append("name", selectedBoatShow.name);
      if ((selectedBoatShow as any)?.file)
        formData.append("image", (selectedBoatShow as any).file);

      const url = selectedBoatShow?.id
        ? `${API_BASE_URL}/boatshows/${selectedBoatShow.id}`
        : `${API_BASE_URL}/boatshows`;

      const method = selectedBoatShow?.id ? "PUT" : "POST";

      await fetch(url, { method, body: formData });
      setModalOpen(false);
      fetchBoatShows();
      setAlert({ type: "success", message: "Saved successfully" });
      window.location.reload();
    } catch (err) {
      setAlert({ type: "error", message: "Save failed" });
    }
  };

  const handleDelete = async () => {
    if (!selectedBoatShow) return;
    try {
      await fetch(`${API_BASE_URL}/boatshows/${selectedBoatShow.id}`, {
        method: "DELETE",
      });
      setDeleteOpen(false);
      fetchBoatShows();
      setAlert({ type: "success", message: "Deleted successfully" });
    } catch (err) {
      setAlert({ type: "error", message: "Delete failed" });
    }
  };

  const paginatedBoatShows = boatShows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ py: 4, bgcolor: "#FFFFFF" }}>
        {alert && (
          <Alert severity={alert.type} sx={{ mb: 3 }}>
            {alert.message}
          </Alert>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography variant="h4" fontWeight="bold" color="text.primary">
            🚤 Boat Shows
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ bgcolor: "#C4B08C", "&:hover": { bgcolor: "#b39d77" } }}
            onClick={() => {
              setSelectedBoatShow({ id: 0, name: "" });
              setModalOpen(true);
            }}
          >
            Add BoatShow
          </Button>
        </Box>

        <Box display="flex" flexWrap="wrap" gap={3} justifyContent="center">
          {paginatedBoatShows.map((b) => (
            <Box
              key={b.id}
              sx={{ flex: "1 1 calc(33.333% - 24px)", minWidth: 250 }}
            >
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 6px 20px rgba(0,0,0,.08)",
                  transition: "transform 0.2s",
                  "&:hover": { transform: "translateY(-4px)" },
                }}
              >
                {b.image && (
                  <CardMedia
                    component="img"
                    height="140"
                    image={`${API_IMG}/boatshows/${b.image}`}
                    alt={b.name}
                    sx={{ borderRadius: "8px 8px 0 0" }}
                  />
                )}
                <CardContent>
                  <Typography variant="h6" color="text.primary">
                    {b.name}
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setSelectedBoatShow(b);
                      setDetailsOpen(true);
                    }}
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setSelectedBoatShow(b);
                      setModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => {
                      setSelectedBoatShow(b);
                      setDeleteOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>

        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(boatShows.length / rowsPerPage)}
            page={page}
            onChange={(e, v) => setPage(v)}
            color="primary"
          />
        </Box>

        {/* --- Modal Add/Edit --- */}
        {modalOpen && selectedBoatShow && (
          <Dialog
            open
            onClose={(event, reason) => {
              // Only close when the X button is clicked
              if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                setModalOpen(false);
              }
            }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle
              sx={{ bgcolor: "#f5f5f5", borderRadius: "8px 8px 0 0" }}
            >
              {selectedBoatShow.id ? "Edit BoatShow" : "Add BoatShow"}
            </DialogTitle>
            <DialogContent
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                label="Name"
                fullWidth
                value={selectedBoatShow.name}
                onChange={(e) =>
                  setSelectedBoatShow({
                    ...selectedBoatShow,
                    name: e.target.value,
                  })
                }
                sx={{ mb: 2, mt: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}
              />

              {/* Upload image container */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  border: "2px dashed #C4B08C",
                  borderRadius: 2,
                  cursor: "pointer",
                  bgcolor: "#fafafa",
                  transition: "all 0.3s",
                  "&:hover": { bgcolor: "#f0f0f0" },
                }}
              >
                {(selectedBoatShow as any).file ? (
                  <img
                    src={URL.createObjectURL((selectedBoatShow as any).file)}
                    alt="preview"
                    style={{
                      width: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                      marginBottom: 8,
                      borderRadius: 8,
                    }}
                  />
                ) : selectedBoatShow.image ? (
                  <img
                    src={`${API_IMG}/boatshows/${selectedBoatShow.image}`}
                    alt="current"
                    style={{
                      width: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                      marginBottom: 8,
                      borderRadius: 8,
                    }}
                  />
                ) : (
                  <Typography color="text.secondary" sx={{ mb: 1 }}>
                    Click to upload image
                  </Typography>
                )}

                <Button
                  variant="contained"
                  component="label"
                  sx={{ bgcolor: "#C4B08C", "&:hover": { bgcolor: "#b39d77" } }}
                >
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files[0]) {
                        setSelectedBoatShow({
                          ...selectedBoatShow,
                          file: files[0],
                        } as BoatShow & { file: File });
                      }
                    }}
                  />
                </Button>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                sx={{ bgcolor: "#C4B08C", "&:hover": { bgcolor: "#b39d77" } }}
                onClick={handleSave}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* --- Modal Details --- */}
        {detailsOpen && selectedBoatShow && (
          <Dialog
            open
            onClose={(event, reason) => {
              // Only close when the X button is clicked
              if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                setDetailsOpen(false);
              }
            }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle
              sx={{ bgcolor: "#f5f5f5", borderRadius: "8px 8px 0 0" }}
            >
              BoatShow Details
            </DialogTitle>
            <DialogContent>
              <Typography variant="h6" color="text.primary">
                {selectedBoatShow.name}
              </Typography>
              {selectedBoatShow.image && (
                <Box mt={2}>
                  <img
                    src={`${API_IMG}/images/boatshows/${selectedBoatShow.image}`}
                    alt={selectedBoatShow.name}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        )}

        {/* --- Modal Delete Confirmation --- */}
        {deleteOpen && selectedBoatShow && (
          <Dialog open onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              setDeleteOpen(false);
            }
          }}>
            <DialogTitle
              sx={{ bgcolor: "#f5f5f5", borderRadius: "8px 8px 0 0" }}
            >
              Confirm Delete
            </DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete{" "}
                <strong>{selectedBoatShow.name}</strong>?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleDelete}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default BoatShowsPage;
